// @MX:ANCHOR: [AUTO] ETL 운영 콘솔 단일 API 모듈 — china-inapi baseURL, 11 endpoints
// @MX:REASON: [AUTO] 모든 EcountEtl 화면(7개)이 이 모듈을 경유. fan_in >= 7
// @MX:SPEC: SPEC-ECOUNT-ETL-OPS-CONSOLE-001 §4, §13.1

import chinaInapi from './chinaInapi';
// @MX:NOTE: [AUTO] axios 인스턴스는 chinaInapi.js에서 공유 모듈로 관리 (SPEC-CHINA-INAPI-COMPANIES-PRIVILEGES-001 B1)
// isTokenRefreshing, failedRequestsQueue, 인터셉터 모두 chinaInapi.js 모듈 스코프에 위치

/**
 * EcountEtl API 모듈 — china-inapi 단일 백엔드 11 endpoints
 * baseURL: VUE_APP_CHINA_INAPI_URL
 * Authorization: Bearer <운영자(XINLAIAN) JWT>
 */
const ecountEtlApi = {
  /**
   * 대시보드 전체 요약 조회 (L1 ETL 메타)
   * markets.CN = -1 sentinel — CN 카운트는 getCnSummary 결과로 합성 필요
   */
  getSummary() {
    return chinaInapi.get('/ecount-etl/summary');
  },

  /**
   * ETL 실행 목록 조회
   * @param {Object} params - { job, status, page, limit }
   */
  getRuns(params = {}) {
    return chinaInapi.get('/ecount-etl/runs', { params });
  },

  /**
   * ETL 실행 상세 조회 (dead-letter 포함 옵션)
   * @param {string} runId - 실행 ID
   * @param {Object} params - { withDeadLetters, deadLetterLimit }
   */
  getRunDetail(runId, params = {}) {
    return chinaInapi.get(`/ecount-etl/runs/${runId}`, { params });
  },

  /**
   * Dead-letter 목록 조회
   * @param {Object} params - { runId, job, stage, reason, dateFrom, dateTo, page, limit }
   */
  getDeadLetters(params = {}) {
    return chinaInapi.get('/ecount-etl/dead-letters', { params });
  },

  /**
   * 품목 마스터 원본 조회 (ecount_product_master)
   * @param {Object} params - { gtinCode, productName, ecountProductCode, gtinChecksumValid, sortBy, sortOrder, page, limit }
   */
  getProducts(params = {}) {
    return chinaInapi.get('/ecount-etl/products', { params });
  },

  /**
   * 거래 이력 조회 (serial_lot_transactions)
   * @param {Object} params - { gtinCode, transactionTypeEnum, transactionDirection, orphan, dateFrom, dateTo, sortBy, sortOrder, page, limit }
   */
  getTransactions(params = {}) {
    return chinaInapi.get('/ecount-etl/transactions', { params });
  },

  /**
   * KR 내수 카탈로그 조회 (gtin_products_kr)
   * @param {Object} params - { gtinCode, productName, page, limit, sortBy, sortOrder }
   */
  getKrProducts(params = {}) {
    return chinaInapi.get('/ecount-etl/kr/products', { params });
  },

  /**
   * KR 내수 카탈로그 요약
   */
  getKrSummary() {
    return chinaInapi.get('/ecount-etl/kr/summary');
  },

  /**
   * CN 수출 카탈로그 조회 (gtin_products_cn) — 커밋 4e5e267 완료
   * @param {Object} params - { gtinCode, productName, page, limit, sortBy, sortOrder }
   */
  getCnProducts(params = {}) {
    return chinaInapi.get('/ecount-etl/cn/products', { params });
  },

  /**
   * CN 수출 카탈로그 요약 — 대시보드 CN 카운트 합성용
   */
  getCnSummary() {
    return chinaInapi.get('/ecount-etl/cn/summary');
  },

  /**
   * 수동 동기화 트리거 (china-inapi → ecount-erp-batch proxy)
   * dryRun=false 시 2단 확인 필수. 502/503 시 batch down 처리.
   * @param {Object} payload - { job, dryRun, limit?, dateFrom?, dateTo?, pageSize? }
   */
  postSyncTrigger(payload) {
    return chinaInapi.post('/ecount-etl/admin/sync-trigger', payload);
  },

  // ── Phase β: 스케줄 설정 관리 (SPEC-ECOUNT-ETL-CRON-CONSOLE-001) ──────────

  /**
   * ETL 스케줄 설정 조회
   * GET /ecount-etl/config → 13 필드 + version (optimistic lock)
   * @MX:NOTE: [AUTO] masterEnabled 포함 — false 시 sub 토글 비활성 + 경고 배너
   */
  getConfig() {
    return chinaInapi.get('/ecount-etl/config');
  },

  /**
   * ETL 스케줄 설정 업데이트 (optimistic lock)
   * PATCH /ecount-etl/config
   * @param {Object} payload - { gtinEnabled?, serialLotEnabled?, expectedVersion }
   * 409 응답: { currentVersion, currentConfig } — state 자동 갱신 후 toast
   */
  updateConfig(payload) {
    return chinaInapi.patch('/ecount-etl/config', payload);
  },

  /**
   * ETL Cron 표현식 변경 (optimistic lock)
   * PATCH /ecount-etl/config/cron
   * @param {Object} payload - { gtinCronExpr?, serialLotCronExpr?, expectedVersion, reason? }
   *   - gtinCronExpr / serialLotCronExpr: 변경할 것만 포함 (최소 1개 필수)
   *   - expectedVersion: 현재 config.version (낙관적 락)
   * 400: cron 유효성 실패 (매분 거부 / 최소 1시간 간격 위반) — message 포함
   * 409: { message, currentVersion, currentConfig } — version 충돌
   * 503: batch 연결 실패
   */
  patchCronConfig(payload) {
    return chinaInapi.patch('/ecount-etl/config/cron', payload);
  },

  /**
   * GTIN 즉시 동기화 트리거
   * POST /ecount-etl/admin/sync-trigger { job: 'gtin' }
   * code=203 응답 → LOGIN_FAIL (D-19)
   */
  triggerGtinSync() {
    return chinaInapi.post('/ecount-etl/admin/sync-trigger', { job: 'gtin' });
  },

  /**
   * Serial-Lot 즉시 동기화 트리거
   * POST /ecount-etl/admin/sync-trigger { job: 'serial-lot' }
   * code=203 응답 → LOGIN_FAIL (D-19)
   */
  triggerSerialLotSync() {
    return chinaInapi.post('/ecount-etl/admin/sync-trigger', { job: 'serial-lot' });
  },

  // ── 변경 이력 + 복원 (딜러 패턴 미러링) ──────────────────────────────────

  /**
   * ETL 스케줄 설정 변경 이력 조회
   * GET /ecount-etl/config/audits?limit=N
   * @param {number} limit - 최대 조회 건수 (기본 20)
   * @returns {Array} audit 항목 배열 (최신순)
   *   shape: { _id, changedAt, fromVersion, toVersion, changedBy, reason,
   *            beforeSnapshot: { gtinSyncEnabled, serialLotSyncEnabled },
   *            afterSnapshot: { gtinSyncEnabled, serialLotSyncEnabled } }
   */
  getConfigAudits(limit = 20) {
    return chinaInapi.get('/ecount-etl/config/audits', { params: { limit } });
  },

  /**
   * ETL 스케줄 설정 복원 (1-click rollback)
   * POST /ecount-etl/config/restore  body: { auditId, expectedVersion }
   * audit beforeSnapshot으로 복원. 새 version 발급 + audit chain 유지.
   * 409: { message, currentVersion, currentConfig } — version 충돌
   * @param {string} auditId - 복원 대상 audit의 _id
   * @param {number} expectedVersion - 현재 config.version (낙관적 락)
   */
  restoreConfig(auditId, expectedVersion) {
    return chinaInapi.post('/ecount-etl/config/restore', { auditId, expectedVersion });
  },

  /**
   * 제품 마스터 대시보드 집계 (SPEC-CHINA-ECOUNT-PRODUCTS-TRANSACTIONS-DASHBOARD-001)
   * KPI 3 + prodType breakdown + topClassCodes top10 + localeBreakdown
   * @param {Object} params - { keyword? }
   */
  getProductsStats(params = {}) {
    return chinaInapi.get('/ecount-etl/products/stats', { params });
  },

  /**
   * 거래 고객사 distinct 목록 (SPEC-CHINA-ECOUNT-PRODUCTS-TRANSACTIONS-DASHBOARD-001)
   * customerCodes multi-select 옵션 populate용 — mount 시 1회 호출
   * @returns {Array} [{ customerCode, customerName }] sorted by customerName asc
   */
  getTransactionsCustomersDistinct() {
    return chinaInapi.get('/ecount-etl/transactions/customers/distinct');
  },

  /**
   * 거래 대시보드 집계 (SPEC-CHINA-ECOUNT-PRODUCTS-TRANSACTIONS-DASHBOARD-001)
   * KPI 3 + monthly 12m + transactionTypeBreakdown + topCustomers top10 + topProducts top10
   * @param {Object} params - { dateFrom?, dateTo?, customerCodes[]?, ecountProductCode?, transactionType? }
   */
  getTransactionsStats(params = {}) {
    return chinaInapi.get('/ecount-etl/transactions/stats', { params });
  },

  // ── SPEC-CHINA-ECOUNT-SALES-LINES-001: 거래상세(交易明细) ──────────────────

  /**
   * 거래상세 목록 조회 (ecount_sales_lines)
   * @MX:NOTE: [AUTO] BE param 6필터 전수 전달 필수 (과거 wrapper 누락 교훈 반영)
   * @param {Object} params - { customerName?, ecountProductCode?, dateFrom?, dateTo?, isReturn?, voucherNo?, page, limit, sortBy?, sortOrder? }
   *   - dateFrom/dateTo: UTC YYYYMMDD 형식 (KST picker 값은 FE에서 변환 후 전달)
   *   - 빈 값 미전달 시 해당 필터 미적용
   */
  getSalesLines(params = {}) {
    return chinaInapi.get('/ecount-etl/sales-lines', { params });
  },

  /**
   * 거래상세 KPI 통계 조회
   * @param {Object} params - 목록과 동일 필터 (customerName?, ecountProductCode?, dateFrom?, dateTo?, isReturn?, voucherNo?)
   * @returns {{ totalLines, totalQty, totalSupplyAmount, totalAmount, returnCount, customerCount }}
   */
  getSalesLinesStats(params = {}) {
    return chinaInapi.get('/ecount-etl/sales-lines/stats', { params });
  },

  // ── SPEC-CHINA-ECOUNT-TX-LINK-001: 연결키 드릴다운 ─────────────────────────

  /**
   * 거래상세 거래처명 distinct 목록 (SPEC-CHINA-ECOUNT-TX-LINK-001 파트 D)
   * customerName 단일 셀렉트 옵션 populate용 — mount 시 1회 호출
   * @returns {Array} [{ customerName }] sorted by customerName asc
   */
  getSalesLinesCustomersDistinct() {
    return chinaInapi.get('/ecount-etl/sales-lines/customers/distinct');
  },

  /**
   * 거래상세 행 → 매칭된 serial_lot_transactions N건 조회 (SPEC-CHINA-ECOUNT-TX-LINK-001 파트 C)
   * @param {string} recordSid - ecount_sales_lines._id (ObjectId string)
   * @param {Object} params - { page?, limit? }
   * @returns 페이지네이션 { data, total, page, limit, totalPages }
   */
  getSalesLineSerialLots(recordSid, params = {}) {
    return chinaInapi.get(`/ecount-etl/sales-lines/${recordSid}/serial-lots`, { params });
  },

  /**
   * serial_lot_transactions 행 → 매칭된 ecount_sales_line 1건 조회 (SPEC-CHINA-ECOUNT-TX-LINK-001 파트 C)
   * matchKey = {tenantKey}_{YYYYMMDD}_{voucherNo}_{lineNo} 파라미터로 lookup
   * @param {Object} params - { tenantKey, transactionDate, voucherNo, lineNo }
   * @returns {{ data: SalesLine | null }}
   */
  getTransactionByMatchKey(params = {}) {
    return chinaInapi.get('/ecount-etl/transactions/by-match-key', { params });
  },
};

export default ecountEtlApi;

// @MX:NOTE: [AUTO] 화면별 named import 지원 — 7개 화면이 개별 함수를 named import로 사용
// default export(axios 인스턴스)와 별개로 named export 유지
export const {
  getSummary,
  getCnSummary,
  getRuns,
  getRunDetail,
  getDeadLetters,
  getProducts,
  getKrProducts,
  getCnProducts,
  getTransactions,
  getKrSummary,
  postSyncTrigger,
  // Phase β
  getConfig,
  updateConfig,
  patchCronConfig,
  triggerGtinSync,
  triggerSerialLotSync,
  // SPEC-CHINA-ECOUNT-PRODUCTS-TRANSACTIONS-DASHBOARD-001
  getProductsStats,
  getTransactionsCustomersDistinct,
  getTransactionsStats,
  // SPEC-CHINA-ECOUNT-SALES-LINES-001
  getSalesLines,
  getSalesLinesStats,
  // SPEC-CHINA-ECOUNT-TX-LINK-001
  getSalesLinesCustomersDistinct,
  getSalesLineSerialLots,
  getTransactionByMatchKey,
  // 변경 이력 + 복원
  getConfigAudits,
  restoreConfig,
} = ecountEtlApi;

/**
 * API 에러에서 사용자 표시용 메시지 추출
 * SPEC-CHINA-ADMIN-LOCALE-PHASE1-001 영역 B REQ-LOCALE-P1-FE-010~017
 * 역호환: 기존 { status, message, type } 필드 보존 (NFR-005)
 * 신규:  { messageKey, messageParams } 추가 — 호출처에서 $t(messageKey, messageParams) 사용
 *
 * @param {Error} error - axios 에러 객체
 * @returns {{ status: number|null, message: string, type: string, messageKey: string, messageParams: object }}
 */
export function extractApiError(error) {
  if (!error.response) {
    // 네트워크 에러 또는 timeout
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      return {
        status: null,
        message: '요청이 시간 초과되었습니다.',
        type: 'timeout',
        messageKey: 'ecountEtl_error_timeout',
        messageParams: {},
      };
    }
    return {
      status: null,
      message: '네트워크 연결을 확인하세요.',
      type: 'network',
      messageKey: 'ecountEtl_error_network',
      messageParams: {},
    };
  }

  const status = error.response.status;
  const serverMessage = error.response.data?.message || '';

  if (status === 403) {
    return {
      status,
      message: '권한이 없습니다 (운영자 Role 필요).',
      type: 'forbidden',
      messageKey: 'ecountEtl_error_forbidden',
      messageParams: {},
    };
  }
  if (status === 404) {
    return {
      status,
      message: serverMessage || '요청한 리소스를 찾을 수 없습니다.',
      type: 'not_found',
      messageKey: 'ecountEtl_error_notFound',
      messageParams: { serverMessage },
    };
  }
  if (status === 503) {
    return {
      status,
      message: 'ETL 배치가 응답하지 않습니다 (ECONNREFUSED). 컨테이너 상태를 확인하세요.',
      type: 'batch_down',
      messageKey: 'ecountEtl_error_batchDown',
      messageParams: {},
    };
  }
  if (status >= 500) {
    return {
      status,
      message: `서버 오류가 발생했습니다 (HTTP ${status}): ${serverMessage || '알 수 없는 오류'}`,
      type: 'server_error',
      messageKey: 'ecountEtl_error_serverError',
      messageParams: { status, serverMessage },
    };
  }

  return {
    status,
    message: serverMessage || '알 수 없는 오류가 발생했습니다.',
    type: 'unknown',
    messageKey: 'ecountEtl_error_unknown',
    messageParams: { serverMessage },
  };
}

/**
 * 페이지네이션 응답 안전 파싱
 * @param {Object} response - axios 응답
 * @returns {{ data: Array, total: number, page: number, limit: number, totalPages: number }}
 */
export function parsePaginatedResponse(response) {
  const raw = response?.data ?? {};
  return {
    data: Array.isArray(raw.data) ? raw.data : [],
    total: Number.isFinite(Number(raw.total)) ? Number(raw.total) : 0,
    page: Number.isFinite(Number(raw.page)) ? Number(raw.page) : 1,
    limit: Number.isFinite(Number(raw.limit)) ? Number(raw.limit) : 20,
    totalPages: Number.isFinite(Number(raw.totalPages)) ? Number(raw.totalPages) : 0,
  };
}
